
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Product } from '../types';

interface ProductListRowProps {
  product: Product;
  onAddToCart: (product: Product, startRect?: DOMRect) => void;
}

const ProductListRow: React.FC<ProductListRowProps> = ({ product, onAddToCart }) => {
  const navigate = useNavigate();
  const [imgError, setImgError] = useState(false);

  const displayImage = (!product.image || imgError) 
    ? `https://placehold.co/600x600/f1f5f9/6366f1?text=${encodeURIComponent(product.name)}`
    : product.image;

  const hasDiscount = product.originalPrice && product.originalPrice > product.price;
  const discountRate = hasDiscount ? Math.round(((product.originalPrice! - product.price) / product.originalPrice!) * 100) : 0;

  const handleAction = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if ((product.options?.length ?? 0) > 0 || (product.addOns?.length ?? 0) > 0) {
      navigate(`/product/${product.id}`);
    } else {
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      onAddToCart(product, rect);
    }
  };

  return (
    <div 
      onClick={() => navigate(`/product/${product.id}`)}
      className={`group relative flex items-center gap-4 md:gap-6 bg-white rounded-[24px] md:rounded-[32px] border ${hasDiscount ? 'border-indigo-100' : 'border-gray-100'} p-3 md:p-4 hover:shadow-xl transition-all duration-500 cursor-pointer`}
    >
      {/* 상품 이미지 */}
      <div className="relative w-24 h-24 md:w-32 md:h-32 shrink-0 overflow-hidden rounded-[20px] md:rounded-[24px] bg-gray-50">
        <img 
          src={displayImage} 
          alt={product.name} 
          onError={() => setImgError(true)}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
        />
        {product.stock <= 0 && (
          <div className="absolute inset-0 bg-black/40 backdrop-blur-[1px] flex items-center justify-center">
            <span className="text-[8px] md:text-[10px] bg-white/90 text-gray-900 font-black px-2 py-1 rounded-lg uppercase italic">Sold Out</span>
          </div>
        )}
      </div>

      {/* 정보 영역 */}
      <div className="flex-1 min-w-0 flex flex-col justify-center">
        <div className="flex flex-wrap gap-2 mb-1.5">
          {product.categories && product.categories.length > 0 && (
            <span className="text-[9px] md:text-[10px] font-black text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-md uppercase">
              {product.categories[0]}
            </span>
          )}
          {product.isNew && (
            <span className="text-[9px] md:text-[10px] font-black text-white bg-indigo-600 px-2 py-0.5 rounded-md uppercase">NEW</span>
          )}
          {hasDiscount && (
            // [Feature 4] 할인 뱃지 색상 변경: Rose -> Blue/Indigo
            <span className="text-[9px] md:text-[10px] font-black text-white bg-blue-600 px-2 py-0.5 rounded-md uppercase animate-pulse">SALE {discountRate}%</span>
          )}
        </div>
        <h3 className="text-sm md:text-lg font-black text-gray-900 truncate leading-tight group-hover:text-indigo-600 transition-colors">
          {product.name}
        </h3>
        <p className="hidden md:block text-xs text-gray-400 mt-2 line-clamp-1 opacity-70">
          품격 있는 메이커 생활의 시작, 메이커스토리와 함께하세요.
        </p>
      </div>

      {/* 가격 및 버튼 영역 */}
      <div className="flex flex-col items-end gap-2 md:gap-3 px-2 md:px-4">
        <div className="text-right">
          {hasDiscount && (
            <span className="block text-[10px] md:text-[11px] font-bold text-gray-400 line-through leading-none mb-1">
              {product.originalPrice?.toLocaleString()}원
            </span>
          )}
          {/* [Feature 4] 가격 색상 변경 */}
          <span className={`text-sm md:text-xl font-black italic ${hasDiscount ? 'text-indigo-600' : 'text-gray-900'}`}>
            {product.price.toLocaleString()}원
          </span>
        </div>
        <button 
          onClick={handleAction}
          disabled={product.stock <= 0}
          // [Feature 4] 버튼 색상 변경
          className={`p-2.5 md:p-3 rounded-xl md:rounded-2xl transition shadow-lg active:scale-90 disabled:bg-gray-100 disabled:text-gray-300 ${hasDiscount ? 'bg-indigo-600 text-white hover:bg-indigo-700' : 'bg-gray-900 text-white hover:bg-indigo-600'}`}
        >
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={3} stroke="currentColor" className="w-4 h-4 md:w-5 md:h-5">
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
        </button>
      </div>
    </div>
  );
};

export default ProductListRow;
